import { z } from 'zod';
import {
  COMMON_ERROR_CODES,
  AUTH_ERROR_CODES,
  QUEST_ERROR_CODES,
  BUDGET_LEDGER_ERROR_CODES,
  CHECKPOINT_ERROR_CODES,
  QUEST_TASK_ERROR_CODES,
  PROOF_ERROR_CODES,
  TEAM_ERROR_CODES,
  CLUB_ERROR_CODES,
  SCENARIO_ERROR_CODES,
  ATTENDANCE_ERROR_CODES,
  LEAVE_ERROR_CODES,
  WORKLOG_ERROR_CODES,
  POINT_ERROR_CODES,
  USER_ERROR_CODES,
  RANK_ERROR_CODES,
  CHAT_ERROR_CODES,
  ASSISTANT_ERROR_CODES,
  SETTINGS_ERROR_CODES,
  UPLOAD_ERROR_CODES,
  AGENT_ERROR_CODES,
  QUEST_TEMPLATE_ERROR_CODES,
  PROPOSAL_ERROR_CODES,
  WIKI_ERROR_CODES,
} from '@aegislab/shared';
import { LEGACY_API_ERROR_CODES } from './legacyApiErrorCodes';

// Only contract constants may become tag values; an arbitrary errorCode can contain PII.
const ERROR_CODES = new Set<string>([
  ...[
    COMMON_ERROR_CODES,
    AUTH_ERROR_CODES,
    QUEST_ERROR_CODES,
    BUDGET_LEDGER_ERROR_CODES,
    CHECKPOINT_ERROR_CODES,
    QUEST_TASK_ERROR_CODES,
    PROOF_ERROR_CODES,
    TEAM_ERROR_CODES,
    CLUB_ERROR_CODES,
    SCENARIO_ERROR_CODES,
    ATTENDANCE_ERROR_CODES,
    LEAVE_ERROR_CODES,
    WORKLOG_ERROR_CODES,
    POINT_ERROR_CODES,
    USER_ERROR_CODES,
    RANK_ERROR_CODES,
    CHAT_ERROR_CODES,
    ASSISTANT_ERROR_CODES,
    SETTINGS_ERROR_CODES,
    UPLOAD_ERROR_CODES,
    AGENT_ERROR_CODES,
    QUEST_TEMPLATE_ERROR_CODES,
    PROPOSAL_ERROR_CODES,
    WIKI_ERROR_CODES,
  ].flatMap((codes) => Object.values(codes)),
  ...LEGACY_API_ERROR_CODES,
]);

// Validation paths can contain user-supplied record keys. Unknown names collapse to
// "other" rather than passing through; add only fixed DTO property names here.
const VALIDATION_FIELDS = new Set([
  'id',
  'userId',
  'teamId',
  'questId',
  'scenarioId',
  'checkpointId',
  'proofId',
  'email',
  'password',
  'name',
  'title',
  'content',
  'description',
  'reason',
  'logDate',
  'date',
  'startDate',
  'endDate',
  'startTime',
  'endTime',
  'type',
  'status',
  'amount',
  'points',
  'reward',
  'difficulty',
  'deadline',
  'rows',
  'items',
  'tasks',
  'attachments',
  'path',
  'filename',
  'mimeType',
  'size',
  'page',
  'limit',
  'cursor',
  'query',
  'search',
  'sort',
  'order',
  'ids',
]);

const responseSchema = z.object({
  errorCode: z.unknown().optional(),
  errors: z.unknown().optional(),
});

export function readApiErrorDiagnostics(data: unknown): {
  readonly error_code: string;
  readonly validation_fields: string[];
} {
  const parsed = responseSchema.safeParse(data);
  const code = parsed.success ? parsed.data.errorCode : undefined;
  const errors = parsed.success ? parsed.data.errors : undefined;
  const fields = (Array.isArray(errors) ? errors.slice(0, 20) : []).map((error: unknown) => {
    if (typeof error !== 'string') return 'other';
    // Zod: "rows.0.content: ..."; class-validator: "email must be an email".
    const path = /^([A-Za-z][A-Za-z0-9_.]*)(?::|\s)/.exec(error)?.[1];
    if (!path) return 'other';
    const segments = path.split('.');
    if (!segments.every((segment) => VALIDATION_FIELDS.has(segment) || /^\d+$/.test(segment))) {
      return 'other';
    }
    return segments.map((segment) => (/^\d+$/.test(segment) ? '[]' : segment)).join('.');
  });
  return {
    error_code: typeof code === 'string' && ERROR_CODES.has(code) ? code : 'unknown',
    validation_fields: [...new Set(fields)],
  };
}
